import React, { useCallback, useEffect, useState } from "react";
import BackArrow from "../../resources/icons/chevron-left.svg";
import EntityConstants from "../../configs/EntityConstants";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { GET } from "../../service/CRUDService";
import DownloadFile from "../../service/DownloadFile";
import Toast from "../../common/Toast/Toast";
import DotMoreOptions from "../../resources/icons/dots-horizontal.svg";
import DiabledDotMoreOptions from "../../resources/icons/disabled-dots-horizontal.svg";
import CredentialPreviewModal from "../../common/CredentialPreviewModal/CredentialPreviewModal";
import CredentialRevokeModal from "../../common/CredentialRevokeModal/CredentialRevokeModal";
import { ReactComponent as RevokeButton } from "../../resources/icons/revokeButton.svg";
import { ReactComponent as DisabledRevokeButton } from "../../resources/icons/disabledRevokeButton.svg";
import JsonPreviewModal from "../../common/JsonPreviewModal/JsonPreviewModal";
import { Menu } from "@headlessui/react";
import DownloadRequestModal from "../../common/DownloadRequestModal/DownloadRequestModal";
import EmailPreviewModal from "../NotifyRecipients/EmailPreviewModal";
import { ReactComponent as PrintIcon } from "../../resources/icons/printer.svg";
import { ReactComponent as PrintIconDisabled } from "../../resources/icons/printer-disabled.svg";
import PrintModal from "../../common/PrintModal/PrintModal";
import { File } from "lucide-react";
import { DownloadIcon, EyeIcon, RefreshCWIcon } from "../../components/icons";
import { useSearchDebounce } from "../../hooks/useSearchDebounce";
import useNavigationStore from "../../store/navigationStore";
import userIcon from "../../resources/icons/user-icon.svg";
import CredentialsDetails from "../../components/DashboardPageComponents/CredentialsDetails";
import DownloadFileCustomName from "../../utils/DownloadFileCustomName";
import { useTranslation } from "react-i18next";
import { emailccRegex } from "../../configs/App.Regex";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { resolveIssuedArtwork } from "../../components/CertificateArtwork/issuedArtwork";

const BATCH_TRANSACTION_POLLING_TIME_INTERVAL = 5000;

const previewArtworkProps = (row) => {
  const { url, artifactType, thumbnailUrl } = resolveIssuedArtwork(row);
  return {
    svg_url: url,
    artifact_type: artifactType,
    thumbnail_url: thumbnailUrl,
  };
};

const printArtworkProps = (row) => {
  const { url, artifactType, thumbnailUrl } = resolveIssuedArtwork(row);
  return { svg_url: url, artifactType, thumbnailUrl };
};

const BatchTransaction = () => {
  const navigate = useNavigate();
  const { transaction_id } = useParams();
  const { t } = useTranslation();
  const { setProgramme, setBatch } = useNavigationStore();
  const location = useLocation();

  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenJSON, setIsOpenJSON] = useState(false);
  const [currentCredentialId, setCurrentCredentialId] = useState(null);
  const [currentCredentialIdJsonModal, setCurrentCredentialIdJsonModal] =
    useState(null);
  const [detailsList, setDetailsList] = useState([]);
  const [programmeId, setProgrammeId] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [credentialTemplateId, setCredentialTemplateId] = useState(null);
  const [issuerCredentialTemplateId, setIssuerCredentialTemplateId] =
    useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [, setIsLoadingcontentList] = useState(true);
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [isStatus, setIsStatus] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(null);
  const [dataLoadCount, setDataLoadCount] = useState(0);
  const [title, setTitle] = useState("");
  const [notificationShowModal, setNotificationShowModal] = useState(null);
  const [isDownloadRequestModalOpen, setIsDownloadRequestModalOpen] =
    useState(false);
  const [downloadRequestModalMessage, setDownloadRequestModalMessage] =
    useState("");
  const [downloadRequestModalHeader, setDownloadRequestModalHeader] =
    useState("");
  const [isDownloadRequestSuccess, setIsDownloadRequestSuccess] =
    useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [currentPrintCredential, setCurrentPrintCredential] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [currentToEmail, setCurrentToEmail] = useState("");
  const [toEmailError, setToEmailError] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [currentCcEmail, setCurrentCcEmail] = useState("");
  const [ccEmailError, setCcEmailError] = useState("");
  const [returnTemplateId, setReturnTemplateId] = useState("");
  const [totalRecipients, setTotalRecipients] = useState(0);
  const [excelData, setExcelData] = useState(null);
  const [zipData, setZipData] = useState(null);
  const [excelFileName, setExcelFileName] = useState(null);
  const [zipFileName, setZipFileName] = useState(null);
  const [programmeName, setProgrammeName] = useState(
    location.state?.programmeName || "",
  );
  const [batchName, setBatchName] = useState(location.state?.batchName || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const toggleModal = (credential_id) => {
    setIsModalOpen(credential_id);
  };

  useEffect(() => {
    transaction_id && GET_ALL_TRANSACTIONS_LIST(limit, offset);
  }, [transaction_id]);

  const formatStatus = (status) => {
    if (!status) return t("history:unavailable");
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const GET_ALL_TRANSACTIONS_LIST = async (limit, offset, search = "") => {
    try {
      setIsRefreshing(true);
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await GET(
        `${EntityConstants.GET_USER_BULK_TRANSACTION_DETAILS}/${transaction_id}?offset=${offset}&limit=${limit}&statuses=Failed,Revoked,Issued,Notified,Printed,Printed_and_Notified${searchParam}`,
      );

      if (response.ok) {
        const data = await response.json();
        count !== data.count && setCount(data.count);
        const resolvedProgrammeName =
          data.programme_name || location.state?.programmeName || "";
        const resolvedBatchName =
          data.batch_name || location.state?.batchName || "";
        setOffset(offset);
        setDetailsList([...data.results]);
        setProgrammeId(data.programme_code);
        setBatchId(data.batch_code);

        setProgrammeName(resolvedProgrammeName);
        setBatchName(resolvedBatchName);
        setCredentialTemplateId(data.credential_template_id);
        setIssuerCredentialTemplateId(data.issuer_credential_template_id);
        setTemplateType(data.template_type);
        dataLoadCount < 1 && setTitle([data.results[0].certificate_name]);
        setPrevious(data.previous);
        setNext(data.next);
        setIsStatus(data.status);
        setTotalRecipients(data.number_of_recipient);
        setExcelData(data.excel_data?.file_url ?? null);
        setZipData(data.zip_file_path?.file_url ?? null);
        setExcelFileName(data.excel_data?.file_name ?? null);
        setZipFileName(data.zip_file_path?.file_name ?? null);
        data.results.length !== 0 && setIsLoadingcontentList(false);

        setProgramme({
          programme_code: data.programme_code,
          programme_name: resolvedProgrammeName,
        });
        setBatch({
          batch_code: data.batch_code,
          batch_name: resolvedBatchName,
        });
      } else {
        console.log(response?.error);
        Toast({
          type: "error",
          title: t("history:error"),
          message: response?.message,
        });
      }
    } catch (error) {
      console.error("Failed to fetch bulk data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setSearchQuery("");
    setIsSearching(false);
    cleanupSearch?.();
    await GET_ALL_TRANSACTIONS_LIST(limit, offset);
  };

  const searchFunction = useCallback(
    async (searchTerm) => {
      await GET_ALL_TRANSACTIONS_LIST(limit, 0, searchTerm);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [limit, transaction_id],
  );

  const { handleSearch, cleanup: cleanupSearch } = useSearchDebounce(
    searchFunction,
    500,
  );

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value.trim()) {
      cleanupSearch();
      setIsSearching(false);
      GET_ALL_TRANSACTIONS_LIST(limit, 0);
    } else {
      setIsSearching(true);
      handleSearch(value, {
        minChars: 1,
        onComplete: () => setIsSearching(false),
        onError: () => setIsSearching(false),
      });
    }
  };

  const CtaList = (status) => [
    {
      actionKey: "preview",
      name: t("history:preview"),
      iconActive: (
        <EyeIcon size={20} className="inline-flex" aria-hidden="true" />
      ),
      iconDisabled: (
        <EyeIcon
          size={20}
          className="inline-flex text-gray-400"
          aria-hidden="true"
        />
      ),
      onClick: (credential_id) => {
        openCloseModal(credential_id);
      },
      disabled: status === "Failed",
    },
    {
      actionKey: "download",
      name: t("history:download"),
      iconActive: (
        <DownloadIcon size={20} className="inline-flex" aria-hidden="true" />
      ),
      iconDisabled: (
        <DownloadIcon
          size={20}
          className="inline-flex text-gray-400"
          aria-hidden="true"
        />
      ),
      onClick: (credential_id) =>
        DownloadFile(
          EntityConstants.DOWNLOAD_SVG_AS_PNG,
          credential_id,
          false,
          showMessageModal,
        ),
      disabled: status === "Failed",
    },
    {
      actionKey: "revoke",
      name: t("history:revoke"),
      iconActive: <RevokeButton className="h-5 w-5" aria-hidden="true" />,
      iconDisabled: (
        <DisabledRevokeButton
          className="h-5 w-5 text-gray-400"
          aria-hidden="true"
        />
      ),
      onClick: (credential_id) => {
        toggleModal(credential_id);
      },
      disabled: status === "Revoked" || status === "Failed",
    },
    {
      actionKey: "print",
      name: t("history:print"),
      iconActive: <PrintIcon className="h-5 w-5" aria-hidden="true" />,
      iconDisabled: (
        <PrintIconDisabled
          className="h-5 w-5 text-gray-400"
          aria-hidden="true"
        />
      ),
      onClick: (credential_id) => {
        const credential = detailsList.find(
          (c) => c.credential_id === credential_id,
        );
        setCurrentPrintCredential(credential);
        setIsPrintModalOpen(true);
      },
      disabled: status === "Failed" || status === "Revoked",
    },
  ];

  const handleDownloadZip = async (event) => {
    event.stopPropagation();
    await DownloadFile(
      EntityConstants.DOWNLOAD_SVG_AS_ZIP,
      transaction_id,
      true,
      showMessageModal,
    );
  };

  const showMessageModal = (isSuccess, status, message) => {
    setIsDownloadRequestSuccess(isSuccess);
    setDownloadRequestModalHeader(status);
    setDownloadRequestModalMessage(message);
    setIsDownloadRequestModalOpen(true);
  };

  const handleToEmailChange = (e) => {
    const email = e.target.value;
    setCurrentToEmail(email);
    if (!email || emailccRegex.test(email)) {
      setToEmailError("");
    }
  };

  const handleAddToEmail = () => {
    if (emailccRegex.test(currentToEmail)) {
      if (toEmails.includes(currentToEmail)) {
        setToEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setToEmails([...toEmails, currentToEmail]);
        setCurrentToEmail("");
        setToEmailError("");
      }
    } else if (currentToEmail) {
      setToEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleRemoveToEmail = (email) => {
    setToEmails(toEmails.filter((item) => item !== email));
    setToEmailError("");
  };

  const handlePasteToEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches =
      pastedText.match(new RegExp(emailccRegex.source, "g")) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (toEmails.includes(email)) {
        setToEmailError(
          `* ${t("workspace:email")} "${email}" ${t("workspace:already_exists")}`,
        );
      } else {
        setToEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setToEmailError(
        `${t("workspace:invalid_emails_list")} ${invalidEmails.join(", ")}`,
      );
    } else {
      setToEmailError("");
    }

    setCurrentToEmail("");
  };

  const handleCcEmailChange = (e) => {
    const email = e.target.value;
    setCurrentCcEmail(email);
    if (!email || emailccRegex.test(email)) {
      setCcEmailError("");
    }
  };

  const handleAddCcEmail = () => {
    if (emailccRegex.test(currentCcEmail)) {
      if (ccEmails.includes(currentCcEmail)) {
        setCcEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setCcEmails([...ccEmails, currentCcEmail]);
        setCurrentCcEmail("");
        setCcEmailError("");
      }
    } else if (currentCcEmail) {
      setCcEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleRemoveCcEmail = (email) => {
    setCcEmails(ccEmails.filter((item) => item !== email));
    setCcEmailError("");
  };

  const handlePasteCcEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");

    const emailMatches =
      pastedText.match(new RegExp(emailccRegex.source, "g")) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (ccEmails.includes(email)) {
        setCcEmailError(
          `* ${t("workspace:email")} "${email}" ${t("workspace:already_exists")}`,
        );
      } else {
        setCcEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setCcEmailError(
        `${t("workspace:invalid_emails_list")} ${invalidEmails.join(", ")}`,
      );
    } else {
      setCcEmailError("");
    }

    setCurrentCcEmail("");
  };

  const openCloseModal = (credential_id) => {
    if (isOpen && currentCredentialId === credential_id) {
      setIsOpen(false);
      setCurrentCredentialId(null);
    } else {
      setIsOpen(true);
      setCurrentCredentialId(credential_id);
    }
  };

  const openCloseJsonModal = (credential_id) => {
    if (isOpenJSON && currentCredentialIdJsonModal === credential_id) {
      setIsOpenJSON(false);
      setCurrentCredentialIdJsonModal(null);
    } else {
      setIsOpenJSON(true);
      setCurrentCredentialIdJsonModal(credential_id);
    }
  };

  const navigateSummaryPage = () => {
    const resolvedProgrammeId = programmeId || location.state?.programmeId;
    const resolvedBatchId = batchId || location.state?.batchId;
    const resolvedProgrammeName =
      programmeName || location.state?.programmeName;
    const resolvedBatchName = batchName || location.state?.batchName;

    navigate(
      `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?templateType=${templateType}`,
      {
        state: {
          programmeName: resolvedProgrammeName,
          batchName: resolvedBatchName,
          programmeId: resolvedProgrammeId,
          batchId: resolvedBatchId,
          programme: {
            programme_code: resolvedProgrammeId,
            programme_name: resolvedProgrammeName,
          },
          batch: {
            batch_code: resolvedBatchId,
            batch_name: resolvedBatchName,
          },
          transaction_id,
        },
      },
    );
  };

  useEffect(() => {
    let pollingInterval;
    if (
      !notificationShowModal &&
      !searchQuery.trim() &&
      (detailsList.some((credential) => !credential.status) ||
        detailsList.length < 1 ||
        isStatus === "In Progress")
    ) {
      pollingInterval = setInterval(() => {
        setDataLoadCount((prev) => prev + 1);
        GET_ALL_TRANSACTIONS_LIST(limit, offset, searchQuery);
      }, BATCH_TRANSACTION_POLLING_TIME_INTERVAL);
    }
    return () => {
      clearInterval(pollingInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    detailsList,
    offset,
    isStatus,
    notificationShowModal,
    limit,
    searchQuery,
  ]);

  useEffect(() => {
    if (!location.state?.reopenEmailPreviewModal) return;

    const returningCredentialId =
      location.state?.reopenCredentialId ||
      location.state?.selectedRecipientIds?.[0];
    const templateIdFromReturn =
      location.state?.createdTemplateId ||
      location.state?.initialTemplateId ||
      "";
    if (!returningCredentialId) return;

    const matchedCredential = detailsList.find(
      (item) => item.credential_id === returningCredentialId,
    );
    if (!matchedCredential) return;

    setToEmails(matchedCredential.email_id ? [matchedCredential.email_id] : []);
    setCurrentToEmail("");
    setToEmailError("");
    setCcEmails([]);
    setCurrentCcEmail("");
    setCcEmailError("");
    setReturnTemplateId(templateIdFromReturn);
    setNotificationShowModal(returningCredentialId);

    const restState = { ...(location.state || {}) };
    delete restState.reopenEmailPreviewModal;
    delete restState.createdTemplateId;
    delete restState.initialTemplateId;
    delete restState.reopenCredentialId;
    delete restState.selectedRecipientIds;
    delete restState.isMultipleSelect;
    navigate(location.pathname, { replace: true, state: restState });
  }, [location.state, detailsList, navigate, location.pathname]);

  return (
    <div
      className="mt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.batchTransaction}
    >
      <div className="flex w-full justify-between py-[2rem] px-[0.75rem]">
        <div className=" flex items-center text-[#101828] font-bold ">
          <button
            id="batch-transaction-back"
            className="flex items-center focus:outline-none"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <img src={BackArrow} alt="" className="pointer-events-none" />
          </button>
          <p>{title}</p>
        </div>
        <div className="flex gap-2">
          {count > 1 && (
            <button
              id="batch-transaction-download-all"
              onClick={(e) => handleDownloadZip(e)}
              className="cursor-pointer py-2.5 px-3 rounded-[8px] font-medium bg-cfPrimaryButton text-cfSecondary"
            >
              <p>{t("history:download_all_certificates")}</p>
            </button>
          )}
          {programmeId &&
            batchId &&
            credentialTemplateId &&
            issuerCredentialTemplateId &&
            templateType && (
              <button
                id="batch-transaction-view-summary"
                className="cursor-pointer py-2.5 px-3 rounded-[8px] font-medium bg-cfPrimary hover:bg-cfPrimaryDark text-cfSecondary"
                onClick={navigateSummaryPage}
              >
                {t("history:view_template_summary")}
              </button>
            )}
        </div>
      </div>
      <DownloadRequestModal
        isOpen={isDownloadRequestModalOpen}
        header={downloadRequestModalHeader}
        message={downloadRequestModalMessage}
        isSuccess={isDownloadRequestSuccess}
        onClose={() => setIsDownloadRequestModalOpen(false)}
      />
      {isModalOpen && (
        <CredentialRevokeModal
          isModalOpen={isModalOpen}
          toggleModal={toggleModal}
          GET_ALL_TRANSACTIONS_LIST={() =>
            GET_ALL_TRANSACTIONS_LIST(limit, offset)
          }
        />
      )}

      {searchQuery.trim() &&
      detailsList.length === 0 &&
      !isSearching ? null : detailsList.length === 0 || isSearching ? (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="relative flex flex-col min-w-0 break-words bg-white shadow-xl border border-[#E0EAFF] rounded-2xl p-6 animate-pulse">
              <div className="flex flex-row items-center justify-between pb-2">
                <div className="h-5 bg-gray-200 rounded-full w-1/3"></div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="rounded-full bg-gray-200 h-12 w-12"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded mt-2 w-1/2"></div>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col min-w-0 break-words bg-white shadow-xl border border-[#E0EAFF] rounded-2xl p-6 animate-pulse">
              <div className="flex flex-row items-center justify-between pb-2">
                <div className="h-5 bg-gray-200 rounded-full w-1/3"></div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded mt-2 w-1/2"></div>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col min-w-0 break-words bg-white shadow-xl border border-[#E0EAFF] rounded-2xl p-6 animate-pulse">
              <div className="flex flex-row items-center justify-between pb-2">
                <div className="h-5 bg-gray-200 rounded-full w-1/3"></div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded mt-2 w-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div
            className={`grid gap-6 ${
              [
                true,
                !!(excelFileName && excelData),
                !!(zipFileName && zipData),
              ].filter(Boolean).length === 1
                ? "grid-cols-1"
                : [
                      true,
                      !!(excelFileName && excelData),
                      !!(zipFileName && zipData),
                    ].filter(Boolean).length === 2
                  ? "grid-cols-1 md:grid-cols-2"
                  : "grid-cols-1 md:grid-cols-3"
            }`}
          >
            <CredentialsDetails
              credentialDescription={`${
                totalRecipients === 1
                  ? t("history:total_recipient")
                  : t("history:total_recipients")
              }`}
              credentialNumber={totalRecipients}
              imageSrc={userIcon}
              backgroundColor="#E0EAFF"
              borderStyling={`border border-[#E0EAFF] ${
                !(excelFileName && excelData) && !(zipFileName && zipData)
                  ? "w-[35rem]"
                  : ""
              }`}
            />
            {excelFileName && excelData && (
              <div className="relative flex flex-col min-w-0 break-words bg-white shadow-xl border border-[#E0EAFF] rounded-2xl p-6">
                <div className="flex flex-row items-center justify-between pb-2">
                  <h5 className="font-bold dark:text-gray-800">
                    {totalRecipients === 1
                      ? t("history:recipient")
                      : t("history:recipients")}{" "}
                    {t("history:data")}
                  </h5>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center min-w-0">
                    {" "}
                    <File className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {excelFileName
                        ? excelFileName
                        : t("history:no_file_available")}
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      DownloadFileCustomName(excelData, excelFileName, t)
                    }
                    className="flex-shrink-0 cursor-pointer"
                    aria-label={`Download ${excelFileName}`}
                  >
                    <DownloadIcon
                      size={16}
                      className="inline-flex text-gray-600"
                      aria-hidden
                    />
                  </button>
                </div>
              </div>
            )}
            {zipFileName && zipData && (
              <div className="relative flex flex-col min-w-0 break-words bg-white shadow-xl border border-[#E0EAFF] rounded-2xl p-6">
                <div className="flex flex-row items-center justify-between pb-2">
                  <h5 className="font-bold dark:text-gray-800">
                    {totalRecipients === 1
                      ? t("history:recipient_certificate")
                      : t("history:recipients_certificates")}
                  </h5>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center min-w-0">
                    {" "}
                    <File className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {zipFileName
                        ? zipFileName
                        : t("history:no_file_available")}
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      DownloadFileCustomName(zipData, zipFileName, t)
                    }
                    className="flex-shrink-0 cursor-pointer"
                    aria-label={`Download ${zipFileName}`}
                  >
                    <DownloadIcon
                      size={16}
                      className="inline-flex text-gray-600"
                      aria-hidden
                    />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="px-6 pb-4 flex items-center justify-between gap-4">
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchInput}
          placeholder={t("history:search_recipients")}
          className="bg-cfSecondary p-2 rounded-lg w-full sm:w-[25rem] self-center md:self-end md:m-0 border border-gray-400"
        />
        <div className="flex items-center gap-2">
          <button
            id="batch-transaction-refresh"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 bg-white ${
              isRefreshing ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <RefreshCWIcon
              size={16}
              className={`inline-flex ${isRefreshing ? "animate-spin" : ""}`}
              aria-hidden
            />
            <span>{t("history:refresh")}</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto z-10 pb-8">
        {!isSearching && searchQuery.trim() && detailsList.length === 0 && (
          <div className="w-full flex justify-center py-10">
            <p
              id="batch-transaction-search-no-results"
              className="text-lg font-semibold text-cfRootBgColor"
            >
              {t("history:no_results_found")}
            </p>
          </div>
        )}
        {(isSearching || (detailsList.length === 0 && !searchQuery.trim())) && (
          <table className="w-full text-sm text-left rtl:text-right text-gray-500">
            <thead className="text-xs text-cfTableHeaderText font-bold bg-cfTableHeaderBackgroundColor">
              <tr>
                <th scope="col" className="px-6 py-3">
                  {t("history:name")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("history:email_id")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("history:phone_number")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("history:status")}
                </th>
                <th scope="col" className="px-6 py-3 whitespace-nowrap">
                  {t("history:actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b hover:bg-[#F9FAFB]">
                <th
                  scope="row"
                  className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap animate-pulse"
                >
                  {t("history:loading_dots")}
                </th>
                <td className="px-6 py-4 animate-pulse whitespace-nowrap ">
                  {t("history:loading_dots")}
                </td>
                <td className="px-6 py-4 animate-pulse whitespace-nowrap ">
                  {t("history:loading_dots")}
                </td>
                <td className=" py-4">
                  <p
                    className={`w-fit font-semibold text-[#EBF8E7] px-[1.125rem] ml-2.5 py-[0.125rem] text-cfSecondary rounded-full bg-gray-300 animate-pulse whitespace-nowrap`}
                  >
                    {t("history:loading_dots")}
                  </p>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {CtaList(detailsList.status).map(
                      ({ iconDisabled }, index) => (
                        <div
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg cursor-not-allowed opacity-40"
                          key={index}
                        >
                          {iconDisabled}
                        </div>
                      ),
                    )}
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg cursor-not-allowed opacity-40">
                      <img
                        src={DiabledDotMoreOptions}
                        alt=""
                        className="h-5 w-5"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {!isSearching && detailsList.length > 0 && (
          <table
            id="batch-transaction-table"
            className="w-full text-sm text-left rtl:text-right text-gray-500 z-20"
          >
            <thead className="text-xs text-cfTableHeaderText font-bold bg-[#E0EAFF]">
              <tr>
                <th scope="col" className="px-6 py-3 text-center">
                  {t("history:name")}
                </th>
                <th scope="col" className="px-6 py-3 text-center">
                  {t("history:email_id")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("history:phone_number")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("history:status")}
                </th>
                <th scope="col" className="px-6 py-3 whitespace-nowrap">
                  {t("history:actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {detailsList.map(
                (
                  {
                    credential_id,
                    approved_by,
                    issuer_name,
                    org_name,
                    public_verify_url,
                    email_id,
                    mobile_number,
                    recipient_name,
                    status,
                    updated_at,
                    whatsapp_number,
                    legacy_pdf,
                  },
                  index,
                  rows,
                ) => (
                  <tr
                    id={`batch-transaction-row-${credential_id}`}
                    className="border-b hover:bg-[#F9FAFB]"
                    key={credential_id}
                  >
                    <th
                      id={`batch-transaction-row-${credential_id}-recipient-name`}
                      scope="row"
                      className="px-6 py-4 text-center font-medium text-gray-900 whitespace-nowrap"
                    >
                      {recipient_name ? (
                        recipient_name
                      ) : (
                        <span className="w-fit font-semibold text-[#EBF8E7] px-[1.125rem]  py-[0.125rem] text-cfSecondary rounded-full bg-gray-300">
                          {t("history:unavailable")}
                        </span>
                      )}
                    </th>
                    <td className="px-6 py-4 text-center">
                      {email_id ? (
                        email_id
                      ) : (
                        <span className="w-fit font-semibold text-[#EBF8E7] px-[1.125rem]  py-[0.125rem] text-cfSecondary rounded-full bg-gray-300">
                          {t("history:unavailable")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {mobile_number ? (
                        mobile_number
                      ) : (
                        <span className="w-fit font-semibold text-[#EBF8E7] px-[1.125rem]  py-[0.125rem] text-cfSecondary rounded-full bg-gray-300">
                          {t("history:unavailable")}
                        </span>
                      )}
                    </td>
                    <td className="py-4">
                      <p
                        id={`batch-transaction-row-${credential_id}-status`}
                        className={`w-fit font-semibold text-[#EBF8E7] px-[1.125rem] ml-2.5 py-[0.125rem] text-cfSecondary rounded-full ${
                          status === "Issued"
                            ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                            : status === "Failed"
                              ? "bg-cfIssuedCredentialTemplateCardStatusUnderReviewBG"
                              : status === "Revoked"
                                ? "bg-cfIssuedCredentialTemplateCardStatusRevokedBG"
                                : status === "Notified"
                                  ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                                  : status === "Printed"
                                    ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                                    : status === "Printed_and_Notified"
                                      ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG"
                                      : !status && " bg-gray-300 "
                        }`}
                      >
                        {formatStatus(status)}
                      </p>
                    </td>
                    <td className="px-6 py-4 z-50">
                      <div className="flex items-center gap-2">
                        {CtaList(status).map(
                          (
                            {
                              actionKey,
                              name,
                              iconActive,
                              iconDisabled,
                              onClick,
                              disabled,
                            },
                            ctaIndex,
                          ) => (
                            <button
                              type="button"
                              id={`batch-transaction-row-${credential_id}-${actionKey}-button`}
                              className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-700 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-1 ${
                                disabled
                                  ? "cursor-not-allowed opacity-40"
                                  : "cursor-pointer hover:bg-[#E0EAFF] hover:text-cfPrimaryDark"
                              }`}
                              key={`${credential_id}-${actionKey}-${ctaIndex}`}
                              title={name}
                              aria-label={name}
                              disabled={disabled}
                              onClick={
                                !disabled
                                  ? () => onClick(credential_id)
                                  : undefined
                              }
                            >
                              {disabled ? iconDisabled : iconActive}
                            </button>
                          ),
                        )}
                        <Menu as="div" className="relative">
                          <Menu.Button
                            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-700 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-1 ${
                              status === "Failed"
                                ? "cursor-not-allowed opacity-40"
                                : "cursor-pointer hover:bg-[#E0EAFF] hover:text-cfPrimaryDark"
                            }`}
                            disabled={status === "Failed"}
                            aria-label={t("history:more_options")}
                            title={t("history:more_options")}
                          >
                            <img
                              src={
                                status === "Failed"
                                  ? DiabledDotMoreOptions
                                  : DotMoreOptions
                              }
                              alt=""
                              className="h-5 w-5"
                              aria-hidden="true"
                            />
                          </Menu.Button>
                          {status !== "Failed" && (
                            <Menu.Items
                              className={`flex flex-col text-gray-500 absolute ${
                                detailsList.length === 1 ||
                                (detailsList.length > 1 &&
                                  (index === detailsList.length - 1 ||
                                    index === detailsList.length - 2))
                                  ? "left-10 -top-5"
                                  : "right-0"
                              } z-10 px-2 text-md w-max origin-top-right rounded-md bg-cfSecondary py-1 shadow-lg ring-1 ring-cfSecondary ring-opacity-5 focus:outline-none`}
                            >
                              <div>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      className={`block w-full text-left px-2 py-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset ${
                                        active ? "bg-gray-100" : ""
                                      }`}
                                      onClick={() => {
                                        openCloseJsonModal(credential_id);
                                      }}
                                    >
                                      {t("history:view_json")}
                                    </button>
                                  )}
                                </Menu.Item>
                                <hr />
                                <Menu.Item
                                  disabled={
                                    status === "Failed" || status === "Revoked"
                                  }
                                >
                                  {({ active, disabled }) => (
                                    <button
                                      type="button"
                                      disabled={disabled}
                                      className={`block w-full text-left px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-inset ${
                                        disabled
                                          ? "cursor-not-allowed opacity-50"
                                          : "cursor-pointer"
                                      } ${active && !disabled ? "bg-gray-100" : ""}`}
                                      onClick={() => {
                                        setToEmails(email_id ? [email_id] : []);
                                        setCurrentToEmail("");
                                        setToEmailError("");
                                        setCcEmails([]);
                                        setCurrentCcEmail("");
                                        setCcEmailError("");
                                        setReturnTemplateId("");
                                        setNotificationShowModal(credential_id);
                                      }}
                                    >
                                      {t("history:send_notification")}
                                    </button>
                                  )}
                                </Menu.Item>
                              </div>
                            </Menu.Items>
                          )}
                        </Menu>
                      </div>
                    </td>
                    {isOpen && currentCredentialId === credential_id && (
                      <CredentialPreviewModal
                        credential_id={credential_id}
                        approved_by={approved_by}
                        issuer_name={issuer_name}
                        org_name={org_name}
                        public_verify_url={public_verify_url}
                        email_id={email_id}
                        mobile_number={mobile_number}
                        recipient_name={recipient_name}
                        status={status}
                        updated_at={updated_at}
                        {...previewArtworkProps(rows[index])}
                        whatsapp_number={whatsapp_number}
                        openCloseModal={openCloseModal}
                        isOpen={isOpen}
                        pdf_url={legacy_pdf}
                      />
                    )}
                    {isOpenJSON &&
                      currentCredentialIdJsonModal === credential_id && (
                        <JsonPreviewModal
                          credential_id={credential_id}
                          openCloseModal={openCloseJsonModal}
                          isOpen={isOpenJSON}
                        />
                      )}
                    {credential_id &&
                      notificationShowModal === credential_id && (
                        <EmailPreviewModal
                          recipient={{
                            id: credential_id,
                            name: recipient_name,
                            email: email_id,
                          }}
                          recipients={[]}
                          isOpen={notificationShowModal === credential_id}
                          onClose={() => {
                            setNotificationShowModal(null);
                            setReturnTemplateId("");
                          }}
                          isMultipleSelect={false}
                          toEmails={toEmails}
                          currentToEmail={currentToEmail}
                          toEmailError={toEmailError}
                          onToEmailChange={handleToEmailChange}
                          onAddToEmail={handleAddToEmail}
                          onRemoveToEmail={handleRemoveToEmail}
                          onPasteToEmail={handlePasteToEmail}
                          ccEmails={ccEmails}
                          setCcEmails={setCcEmails}
                          currentCcEmail={currentCcEmail}
                          ccEmailError={ccEmailError}
                          onCcEmailChange={handleCcEmailChange}
                          onAddCcEmail={handleAddCcEmail}
                          onRemoveCcEmail={handleRemoveCcEmail}
                          onPasteCcEmail={handlePasteCcEmail}
                          initialTemplateId={returnTemplateId}
                        />
                      )}
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between mt-6 px-8 w-full">
        <button
          id="batch-transaction-pagination-previous"
          onClick={() =>
            GET_ALL_TRANSACTIONS_LIST(limit, offset - limit, searchQuery)
          }
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={previous === null}
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>{t("history:previous")}</span>
          </span>
        </button>

        <button
          id="batch-transaction-pagination-next"
          onClick={() =>
            GET_ALL_TRANSACTIONS_LIST(limit, offset + limit, searchQuery)
          }
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          disabled={next === null}
        >
          <span className="flex items-center justify-center gap-2">
            <span>{t("history:next")}</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>
      {currentPrintCredential && (
        <PrintModal
          isOpen={isPrintModalOpen}
          toggleModal={setIsPrintModalOpen}
          {...printArtworkProps(currentPrintCredential)}
          pdf_url={currentPrintCredential.legacy_pdf}
          credential_id={currentPrintCredential.credential_id}
        />
      )}
    </div>
  );
};

export default BatchTransaction;
